import { useCallback } from 'react';
import uuid from 'uuid/v4';

import {
	useAnalyticsEvents,
	type AnalyticsEventPayload,
	type UIAnalyticsEvent,
} from '@atlaskit/analytics-next';
import { defineMessages, useIntl } from 'react-intl';

import { CONFLUENCE_AIFC_PAGE_CREATE_KEY } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import {
	createHookWithAssistanceService,
	type AssistanceServiceInjectedParams,
	type Dialogues,
	type MessagePayloadAction,
} from '@atlassian/conversation-assistant-service';
import {
	useChatContextStoreActions,
	useChatMessagesActions,
	useStagingAreaActions,
	isStoreAssistant,
	type StoreChatItem,
	type StoreFollowUp,
} from '@atlassian/conversation-assistant-store';
import { useSmartCreationChatStoreApi } from '@atlassian/conversation-assistant/controllers/smart-creation-api';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

export type OpenModalWithTemplateParams = {
	contentId: string;
	adfBody: string;
	templateName: string;
	spaceKey: string;
	onOpenCallback?: () => void;
};

const i18n = defineMessages({
	createFromTemplate: {
		id: 'smart-create.open-modal-with-template.create-from-template',
		defaultMessage: 'Create from template: {templateName}',
		description:
			'Message shown in the Rovo chat history representing the user intent to create a page from a template.',
	},
	templateSelectedHeading: {
		id: 'smart-create.open-modal-with-template.template-selected-heading',
		defaultMessage: "Here's the template you selected. Let's start editing together.",
		description:
			'Heading shown above the template preview card when a user selects a template from the gallery.',
	},
	templateSelectedDescription: {
		id: 'smart-create.open-modal-with-template.template-selected-description',
		defaultMessage:
			'Ask for recommended next steps or tell me exactly how you would like the template filled out.',
		description:
			'Description text shown below the bold heading above the template preview card when a user selects a template from the gallery.',
	},
	recentWork: {
		id: 'smart-create.open-modal-with-template.recent-work',
		defaultMessage: 'Fill this template using recent work',
		description:
			'Message shown in the Rovo chat follow up prompting Rovo to fill the template using recent work.',
	},
	recommendation: {
		id: 'smart-create.open-modal-with-template.recommendation',
		defaultMessage: 'What do you recommend doing?',
		description: 'Message shown in the Rovo chat follow up prompting Rovo to recommend what to do.',
	},
});

/**
 * Opens the Create with Rovo smart creation modal pre-loaded with template content.
 *
 * Bypasses the normal generation step by creating a real backend conversation
 * via createConversationChannel with a pre-seeded CONFLUENCE_AIFC_PAGE_CREATE_KEY
 * action carrying the template ADF. The caller must create a staged draft first and pass its contentId.
 */
const useOpenModalWithTemplateImpl = ({
	assistanceServiceInjectedParams: { assistanceService },
}: AssistanceServiceInjectedParams) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { setTarget } = useChatContextStoreActions();
	const { setStagingAreaMode, resetStagingArea, setMessageIdSelectedForPreview } =
		useStagingAreaActions();
	const { openModal } = useSmartCreationModalActions();
	const { seedConversation, processConversationMessages } = useSmartCreationChatStoreApi();
	const { setMessages } = useChatMessagesActions();

	return useCallback(
		async ({
			contentId,
			adfBody,
			templateName,
			spaceKey,
			onOpenCallback,
		}: OpenModalWithTemplateParams) => {
			const invocationId = uuid();

			// Build the action payload to be included in the agent message.
			const action: MessagePayloadAction = {
				key: CONFLUENCE_AIFC_PAGE_CREATE_KEY,
				invocation_id: invocationId,
				planned_data: {
					title: templateName,
					adf: adfBody,
					contentId,
					type: 'page',
				},
				status: 'FINISHED',
				display_name: formatMessage(i18n.createFromTemplate, { templateName }),
			};

			// Build the dialogue (human + agent messages) to seed the conversation.
			const dialogue: Dialogues = {
				human_message: {
					content: '',
				},
				agent_message: {
					content: [
						`## ${formatMessage(i18n.templateSelectedHeading)}`,
						formatMessage(i18n.templateSelectedDescription),
					].join('\n\n'),
					mimeType: 'text/markdown',
					actions: [action],
				},
			};

			// Create backend conversation with the seeded action
			const channel = await assistanceService.createConversationChannel({
				dialogues: [dialogue],
			});

			const conversationId = channel.id;

			// Register the staged draft as the staging-area target
			setTarget('staging-area', {
				id: contentId,
				type: 'page',
				canProvideContext: true,
			});

			// Register the conversation using seedConversation (a plain setState action).
			// createConversationOptimistically/createSeededConversation use createSafeAction
			// which requires ChatStoreContainer's containerProps — unavailable here since
			// the template gallery and GlobalComponents are siblings of RightPanel.
			// skipNextFetch: true prevents ChatStoreContainer from re-fetching the conversation
			// on mount, which would overwrite the locally-seeded messages including follow-ups.
			seedConversation({ conversationId, skipNextFetch: true });

			// Fetch and transform messages into the store format.
			const chat = await assistanceService.getChat(conversationId);

			const storeMessages: StoreChatItem[] = processConversationMessages({
				latestMessages: chat ?? [],
				conversationId,
				sendAnalyticsEvent: (payload: AnalyticsEventPayload | UIAnalyticsEvent) =>
					createAnalyticsEvent(payload).fire(),
			});

			if (storeMessages.length > 0) {
				// Required so useSelectedPreview knows which message drives the finalize popup.
				const assistantMsg = storeMessages.find(isStoreAssistant);
				const assistantMsgId = assistantMsg?.content?.message?.message?.id;

				// Append synthetic follow-up prompts directly. Since seedConversation was called
				// with skipNextFetch: true, ChatStoreContainer will not re-fetch and overwrite these.
				const followUpItem: StoreFollowUp = {
					type: 'FOLLOW_UP',
					content: {
						type: 'FOLLOW_UP_QUERIES',
						message: {
							follow_ups: [
								{ type: 'SYNTHETIC', text: formatMessage(i18n.recentWork) },
								{ type: 'SYNTHETIC', text: formatMessage(i18n.recommendation) },
							],
						},
						millisOffset: 0,
					},
					id: Date.now(),
				};

				setMessages(conversationId, [...storeMessages, followUpItem]);

				if (assistantMsgId) {
					setMessageIdSelectedForPreview(assistantMsgId);
				}
			}

			// Mark as template flow so follow-up prompts target the existing staged draft.
			setStagingAreaMode('modal', 'template-smartCreate');

			openModal({
				entryPoint: 'template-smartCreate',
				spaceKey,
				stagingAreaActive: true,
				content: {
					id: contentId,
					type: 'page',
				},
				// Prevents re-open loops — handleClose doesn't call resetStagingArea by default.
				onCloseCallback: resetStagingArea,
				onOpenCallback,
			});
		},
		[
			assistanceService,
			createAnalyticsEvent,
			formatMessage,
			openModal,
			processConversationMessages,
			resetStagingArea,
			seedConversation,
			setMessageIdSelectedForPreview,
			setMessages,
			setStagingAreaMode,
			setTarget,
		],
	);
};

export const useOpenModalWithTemplate = createHookWithAssistanceService(
	useOpenModalWithTemplateImpl,
);
